<template>
    <div>
        <div v-if="hasReactions" class="reactionGroup">
            <template v-for="(count, reactionType) in visibleReactions" :key="reactionType">
                <transition name="reaction-scale" mode="out-in">
                    <div v-if="count > 0" class="reactionBox" :class="{ active: isSelectedReaction(reactionType) }">
                        <el-popover :width="180" placement="top" :disabled="true" 
                            popper-style="border: 1px solid var(--base-new-border); padding: 15px; border-radius: 8px; background: #2a2a2a; color: var(--base-text-white);">

                            <template #reference>
                                <div class="reactionContent" @click.stop="openDetailPopup">
                                    <div>
                                        <figure>
                                            <img :src="getReactionImage(reactionType)" @error="handleImageError"
                                                :alt="reactionType" />
                                        </figure>
                                    </div>
                                    <span class="reactionBoxTitle">{{ count }}</span>
                                </div>
                            </template>
                        </el-popover>
                    </div>
                </transition>
            </template>

            <transition name="reaction-fade">
                <div v-if="hasRemainingReactions" class="allReactions">
                    <el-popover :width="180" class="cardStyled"
                        popper-style="border: 1px solid var(--base-new-border); padding: 15px; border-radius:8px;"
                        :popper-options="{ autoHide: true }">

                        <template #reference>
                            <div v-if="remainingCount > 0" class="remainingCount">+{{ remainingCount }}</div>
                        </template>

                        <template #default>
                            <div class="remainingReactionGroup">
                                <template v-for="(count, reactionType) in allReactions"
                                    :key="reactionType">
                                    <transition name="reaction-bounce" mode="out-in">
                                        <div v-if="count > 0" class="reactionBox" :class="{ active: isSelectedReaction(reactionType) }"
                                            @click.stop="openDetailPopup">
                                            <div>
                                                <figure>
                                                    <img :src="getReactionImage(reactionType)" @error="handleImageError"
                                                        :alt="reactionType" />
                                                </figure>
                                            </div>
                                            <el-tooltip :content="reactionType" placement="top">
                                                <span class="reactionBoxTitle">{{ count }}</span>
                                            </el-tooltip>
                                        </div>
                                    </transition>
                                </template>
                            </div>
                        </template>
                    </el-popover>
                </div>
            </transition>
        </div>
        
        <transition name="reaction-fade">
            <div v-if="!hasReactions" class="no-reactions">
                <!-- No reactions yet -->
            </div>
        </transition>
    </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from 'vue'
import { pushModal } from "jenesius-vue-modal";
import PostReactionDetailPopup from "./PostReactionDetailPopup.vue";
import { useReactionStore } from '@/services/reaction.store';

export default defineComponent({
    props: ["post", "reactions", "selectedReaction"],
    setup(props, { emit }) {
        const reactionStore = useReactionStore();
        const currentSourceId = ref(props.post?._id);
        const currentSelectedReaction = ref(props.selectedReaction);
        
        // Configuration - change this number to adjust how many visible reactions to show
        const MAX_VISIBLE_REACTIONS = 3; // Reduced from 8 to 3
        
        // Make isSelectedReaction a computed property for reactivity
        const isSelectedReaction = computed(() => (reactionType) => {
            return currentSelectedReaction.value === reactionType;
        });
        
        // Watch for changes in selectedReaction prop with immediate option
        watch(() => props.selectedReaction, (newReaction) => {
            currentSelectedReaction.value = newReaction;
        }, { immediate: true });

        // Prefer live Firebase store data; fallback to embedded post data
        const reactionData = computed(() => {
            reactionStore.reactionCountsVersion;
            if (props.post?._id) {
                const fromStore = reactionStore.getAllReactionCountsForPost(props.post._id);
                if (Object.keys(fromStore).length > 0) {
                    return fromStore;
                }
            }
            return props.post?.reactionCounts || {};
        });

        // All reaction types with counts > 0 from reactionCounts
        const allReactions = computed(() => {
            if (!reactionData.value) {
                return {};
            }

            const result = {};
            for (const [reactionType, count] of Object.entries(reactionData.value)) {
                if (Number(count) > 0) {
                    result[reactionType] = Number(count);
                }
            }
            return result;
        });

        const visibleReactions = computed(() => {
            if (!allReactions.value) return {};
            
            const result = {};
            let count = 0;
            
            // Get first MAX_VISIBLE_REACTIONS reactions
            for (const reactionType of Object.keys(allReactions.value)) {
                if (count < MAX_VISIBLE_REACTIONS) {
                    result[reactionType] = allReactions.value[reactionType];
                    count++;
                } else {
                    break;
                }
            }
            emit('update-total-count', Object.keys(allReactions.value).length);
            return result;
        });

        const remainingReactions = computed(() => {
            if (!allReactions.value) return {};
            
            const result = {};
            let count = 0;
            
            // Get reactions beyond the first MAX_VISIBLE_REACTIONS
            for (const reactionType of Object.keys(allReactions.value)) {
                if (count >= MAX_VISIBLE_REACTIONS) {
                    result[reactionType] = allReactions.value[reactionType];
                }
                count++;
            }
            
            return result;
        });

        const hasReactions = computed(() => {
            return Object.keys(allReactions.value).length > 0;
        });

        const hasRemainingReactions = computed(() => {
            return Object.keys(remainingReactions.value).length > 0;
        });

        const remainingCount = computed(() => {
            return Object.keys(remainingReactions.value).length;
        });

        const totalReactionsCount = computed(() => {
            return Object.keys(allReactions.value).length;
        });

        const openDetailPopup = async () => {
            if (!props.post?._id) return;
            
            const modal = await pushModal(PostReactionDetailPopup, { 
                sourceId: props.post._id 
            });
            modal.on("close", () => { });
        };

        const handleImageError = (event) => {
            event.target.style.display = 'none';
        };

        const getReactionImage = (reactionName) => {
            return require(`../../../../../assets/images/reactions/${reactionName}.png`);
        };

        return {
            visibleReactions,
            remainingReactions,
            allReactions,
            remainingCount,
            totalReactionsCount,
            openDetailPopup,
            hasReactions,
            hasRemainingReactions,
            handleImageError,
            getReactionImage,
            currentSourceId,
            isSelectedReaction,
            currentSelectedReaction
        }
    }
})
</script>

<style lang="scss" scoped>
/* Optimized styles for better space usage */
.remainingCount {
    color: var(--base-text);
    padding: 2px 8px;
    font-size: var(--font-xs);
    background: var(--base-lightest);
    border-radius: 12px;
    margin-inline-start: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        background: var(--base-light);
        transform: scale(1.05);
    }
}

.remainingReactionGroup {
    padding: 4px 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: 200px;
}

.reactionBox {
    display: flex;
    align-items: center;
    margin-inline-end: 6px; /* Reduced from 8px */
    background: var(--base-lightest);
    border-radius: 50px;
    padding: 4px 6px; /* Reduced padding */
    gap: 2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &.active {
        background: var(--base-primary);
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        .reactionBoxTitle {
            color: var(--base-text-white);
        }
    }

    &:hover:not(.active) {
        transform: translateY(-2px);
        background: var(--base-light);
        transition: all 0.2s ease;
    }

    figure {
        img {
            width: 20px; /* Reduced from 24px */
            transition: transform 0.3s ease;
        }
    }

    &:hover figure img {
        transform: scale(1.1);
    }

    .reactionBoxTitle {
        font-weight: 500;
        font-size: var(--font-2xs); /* Smaller font */
        color: var(--base-text);
        padding-inline-start: 4px; /* Reduced padding */
        padding-top: 1px;
        margin: 0;
        max-width: 28px; /* Reduced width */
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        transition: color 0.3s ease;
    }
}

.reactionGroup {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    transition: all 0.3s ease;

    .allReactions {
        display: flex;
        align-items: center;
    }
}

/* Keep your existing transition effects the same */
.reaction-scale-enter-active,
.reaction-scale-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.reaction-scale-enter-from {
    opacity: 0;
    transform: scale(0.8) translateY(10px);
}

.reaction-scale-leave-to {
    opacity: 0;
    transform: scale(0.8) translateY(-10px);
}

.reaction-scale-enter-to,
.reaction-scale-leave-from {
    opacity: 1;
    transform: scale(1) translateY(0);
}

.reaction-fade-enter-active,
.reaction-fade-leave-active {
    transition: all 0.4s ease;
}

.reaction-fade-enter-from,
.reaction-fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}

.reaction-bounce-enter-active {
    animation: reaction-bounce-in 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.reaction-bounce-leave-active {
    animation: reaction-bounce-out 0.3s ease;
}

@keyframes reaction-bounce-in {
    0% {
        opacity: 0;
        transform: scale(0.3) rotate(-5deg);
    }
    50% {
        opacity: 0.7;
        transform: scale(1.1) rotate(2deg);
    }
    100% {
        opacity: 1;
        transform: scale(1) rotate(0);
    }
}

@keyframes reaction-bounce-out {
    0% {
        opacity: 1;
        transform: scale(1);
    }
    100% {
        opacity: 0;
        transform: scale(0.5);
    }
}

/* Responsive design for different screen sizes */
@media (max-width: 768px) {
    .reactionBox {
        margin-inline-end: 4px;
        padding: 3px 5px;
        
        figure img {
            width: 18px;
        }
        
        .reactionBoxTitle {
            font-size: 10px;
            max-width: 24px;
            padding-inline-start: 2px;
        }
    }
    
    .remainingCount {
        padding: 2px 6px;
        font-size: 10px;
    }
}

@media(min-width: 1280px) and (max-width: 1891px) {
    .reactionGroup {
        .reactionBox {
            padding: 4px 6px;
            gap: 1px;

            .reactionBoxTitle {
                font-size: 10px;
            }

            figure {
                img {
                    width: 16px
                }
            }
        }
    }
}

.userList {
    padding: 10px;

    .userItem {
        padding-bottom: 4px;

        &:last-child {
            padding-bottom: 0
        }

        .userName {
            color: var(--base-text-white);
            font-size: var(--font-2xs);
            font-weight: 400;
            text-overflow: ellipsis;
            overflow: hidden;
            display: block;
            white-space: nowrap;
            max-width: 160px;
        }
    }

    .moreUsers {
        padding: 8px 0 4px 0;
        color: rgba(255, 255, 255, 0.7);
        font-size: 13px;
        font-style: italic;
    }
}

.reactionContent {
    display: flex;
    align-items: center;
    gap: 2px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:active {
        transform: scale(0.95);
    }
}

.reactionGroup {
    animation: slideInUp 0.5s ease-out;
}

@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>